import { init, browserTracingIntegration } from "@sentry/remix";
import { RemixBrowser, useLocation, useMatches } from "@remix-run/react";
import { startTransition, StrictMode, useEffect } from "react";
import { hydrateRoot } from "react-dom/client";
import i18n from "./i18n/config";
import i18next, { use } from "i18next"
import { I18nextProvider, initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import Backend from "i18next-http-backend";
import { getInitialNamespaces } from "remix-i18next/client";

init({
    dsn: "https://d7d4d7e33704446f33f2bb312174d3aa@o4509951110086656.ingest.de.sentry.io/4509951111594064",
    tracesSampleRate: 1,
    enableLogs: true,
    environment: import.meta.env.MODE,
    enabled: import.meta.env.PROD,

    integrations: [browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches
    })],

    denyUrls: [
      // Microsoft Clarity
      /clarity\.js/i,
      /clarity\.ms/i,
      // Usercentrics CMP
      /usercentrics\.eu/i,
      // Google Maps internal errors
      /maps\.googleapis\.com/i,
    ],

    ignoreErrors: [
      // Third-party: Clarity
      "Aa[l].children",
      // Third-party: Usercentrics dynamic import failures
      "Failed to fetch dynamically imported module",
      // Google Maps component loading failures
      'Could not load "marker"',
      'Could not load "onion"',
      // Hydration errors (typically caused by browser extensions modifying DOM)
      "There was an error while hydrating",
      // Network errors (transient)
      "Load failed",
      "Failed to fetch",
    ],
})

async function hydrate() {
  await use(initReactI18next) // Tell i18next to use the react-i18next plugin
    .use(LanguageDetector) // Setup a client-side language detector
    .use(Backend) // Setup your backend
    .init({
      ...i18n, // spread the configuration
      // This function detects the namespaces your routes rendered while SSR use
      ns: getInitialNamespaces(),
      backend: { loadPath: "/locales/{{lng}}/{{ns}}.json" },
      detection: {
        // Here only enable htmlTag detection, we'll detect the language only
        // server-side with remix-i18next, by using the `<html lang>` attribute
        // we can communicate to the client the language detected server-side
        order: ["htmlTag"],
        // Because we only use htmlTag, there's no reason to cache the language
        // on the browser, so we disable it
        caches: [],
      },
      react: { useSuspense: false },
    });

  startTransition(() => {
    hydrateRoot(
      document,
      <I18nextProvider i18n={i18next}>
        <StrictMode>
          <RemixBrowser />
        </StrictMode>
      </I18nextProvider>
    );
  });
}

if (window.requestIdleCallback) {
  window.requestIdleCallback(hydrate);
} else {
  // Safari doesn't support requestIdleCallback
  // https://caniuse.com/requestidlecallback
  window.setTimeout(hydrate, 1);
}